import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { motion } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Package, Smartphone, Sparkles } from 'lucide-react';

export interface DiscoveryStripProps {
    type?: 'brands' | 'covers' | 'skins' | 'cards' | string;
    items?: any[];
    totalCount?: number;
    onClick?: () => void;
    brands?: any[];
    skins?: any[];
    cards?: any[];
    onNavigate?: (page: string, category?: any) => void;
}

const DEFAULT_CONFIG = { label: 'Items', icon: <Sparkles size={10} className="text-blue-400" />, color: 'blue' };

const CONFIG_MAP: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    brands: { label: 'Brands', icon: <Package size={10} className="text-blue-400" />, color: 'blue' },
    covers: { label: 'Covers', icon: <Smartphone size={10} className="text-purple-400" />, color: 'purple' },
    skins: { label: 'Skins', icon: <Smartphone size={10} className="text-purple-400" />, color: 'purple' },
    cards: { label: 'Premium Drops', icon: <Sparkles size={10} className="text-blue-400" />, color: 'blue' }
};

const DiscoverySingleStrip = memo(function DiscoverySingleStrip({
    type = 'cards',
    items = [],
    totalCount = 0,
    onClick
}: DiscoveryStripProps) {
  const { t } = useTranslation(['common', 'marketplace', 'store', 'profile', 'rules']);
    const { impactOccurred } = useHapticFeedback();

    const config = (type && CONFIG_MAP[type]) || DEFAULT_CONFIG;
    const displayItems = Array.isArray(items) ? items.slice(0, 4) : [];
    const activeCount = totalCount > 0 ? totalCount : (items?.length || 0);
    const overflowCount = Math.max(0, activeCount - 4);

    return (
        <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => {
                impactOccurred('light');
                onClick?.();
            }}
            className="flex items-center gap-3 bg-white/[0.03] backdrop-blur-xl border border-white/[0.05] p-1.5 pr-4 rounded-2xl active:scale-95 transition-all group shrink-0 h-14"
        >
            <div className="flex -space-x-3 items-center ml-1">
                {displayItems.length > 0 ? (
                    displayItems.map((item, idx) => {
                        const rawImage = item?.image || item?.image_filename || item?.profileImage;
                        const imgSource = rawImage 
                            ? (rawImage.startsWith('/') || rawImage.startsWith('http') ? rawImage : `/card_Image/${rawImage}`) 
                            : '/icons/default-avatar.svg';

                        return (
                            <motion.div
                                key={item?.id || idx}
                                initial={{ x: -10, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className="relative"
                                style={{ zIndex: displayItems.length - idx }}
                            >
                                <div className="w-8 h-8 rounded-full border-2 border-[#0A0E17] bg-tg-secondary overflow-hidden shadow-lg group-hover:border-white/20 transition-colors">
                                    <img
                                        src={imgSource}
                                        alt={type || 'item'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                                    />
                                </div>
                            </motion.div>
                        );
                    })
                ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-[#0A0E17] bg-white/[0.05] flex items-center justify-center relative" style={{ zIndex: 1 }}>
                        {config.icon}
                    </div>
                )}
                {/* Overflow Placeholder */}
                {overflowCount > 0 && (
                    <div 
                        className="w-8 h-8 rounded-full border-2 border-[#0A0E17] bg-white/[0.05] backdrop-blur-md flex items-center justify-center text-[9px] font-bold text-white/50 relative"
                        style={{ zIndex: 0 }}
                    >
                        +{overflowCount}
                    </div>
                )}
            </div>

            {/* Status Info */}
            <div className="flex flex-col items-start text-left">
                <span className="text-[8px] font-black text-white/30 uppercase tracking-[0.2em] flex items-center gap-1">
                    {config.icon}
                    {t(`common:${config.label === 'Premium Drops' ? 'premiumDrops' : config.label.toLowerCase()}`, config.label)}
                </span>
                <div className="flex items-center gap-1 mt-0.5">
                    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-${config.color}-500/10 border border-${config.color}-500/20`}>
                        <span className={`w-1 h-1 bg-${config.color}-500 rounded-full animate-ping`} />
                        <span className={`text-[8px] font-bold text-${config.color}-500 uppercase tracking-tighter`}>{t('common:hot', 'HOT')}</span>
                    </div>
                    <p className={`text-[9px] text-${config.color}-400 font-bold uppercase tracking-wider`}>
                        {activeCount.toLocaleString()}
                    </p>
                </div>
            </div>
        </motion.button>
    );
});

export const DiscoveryStrip = memo(function DiscoveryStrip(props: DiscoveryStripProps) {
    const { t } = useTranslation(['common', 'marketplace', 'store', 'profile', 'rules']);

    if (props.brands || props.skins || props.cards) {
        const brands = props.brands || [];
        const skins = props.skins || [];
        const cards = props.cards || [];
        const onNavigate = props.onNavigate;

        return (
            <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
                {brands.length > 0 && (
                    <DiscoverySingleStrip
                        type="brands"
                        items={brands}
                        totalCount={brands.length}
                        onClick={() => onNavigate?.('all_items', t('common:brands', 'Brands'))}
                    />
                )}
                {skins.length > 0 && (
                    <DiscoverySingleStrip
                        type="skins"
                        items={skins}
                        totalCount={skins.length}
                        onClick={() => onNavigate?.('all_items', t('common:skins', 'Skins'))}
                    />
                )}
                {cards.length > 0 && (
                    <DiscoverySingleStrip
                        type="cards"
                        items={cards}
                        totalCount={cards.length}
                        onClick={() => onNavigate?.('all_items', 'Premium Market Drop')}
                    />
                )}
            </div>
        );
    }

    return <DiscoverySingleStrip {...props} />;
});
